import { useEffect, useState } from "react";
import { Row, Col, Form, Button, Select, Radio, Tag, Spin, message, Tooltip, Alert } from 'antd';
import { admissionService, serviceConditions } from "../../../services/admission";
import { laboratoryService } from "../../../services/laboratory";
import debounce from "lodash/debounce";
import uniqBy from "lodash/uniqBy";
import LaboratoryExams from './LaboratoryExams';
import { utils } from "../../../utils/messageAlerts";
import evaluationTypes from "../../../utils/evaluationType.json";
import { useTranslation } from 'react-i18next';
import countrySettings from '../countrySettings.json'
import { isProfessionRequiredByEvaluationType } from '../../../utils/evaluationRules';
import { PRESTATIONS_SEARCH_DEBOUNCE_MS, hasEnoughSearchLength } from '../../../utils/searchConfig';

const { Option } = Select;

const PatientServices = ({
  reservation,
  patientToAdmission,
  setPatientToAdmission,
  currentStep,
  setCurrentStep,
  providerCode,
  countryCode,
  setLaboratoryLoading,
  laboratoryLoading,
  laboratoryModule
}) => {
  const [servicesList, setServicesList] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [laboratoryPrestations, setLaboratoryPrestations] = useState([]);
  const [previousLaboratoryPrestations, setPreviousLaboratoryPrestations] = useState([]);
  const [serviceForm] = Form.useForm();
  const { t } = useTranslation();
  const diabeticId = '93';
  const [showEvaluationTypeTooltip, setShowEvaluationTypeTooltip] = useState(false);
  const [showProfessionWarning, setShowProfessionWarning] = useState(false);
  const professionRequiredByCountry = countrySettings.patientForm.professionRequired?.[countryCode] ?? false;

  const confirmRefreshPage = (event) => {
    event.preventDefault();
    const message = "¿Estás seguro que deseas abandonar?";
    return (event.returnValue = message);
  };

  const viewLaboratoryPrestations = (labsPrestations) => {
    let result = labsPrestations;
    const laboratoryServices = uniqBy(result, 'name');
    return laboratoryServices;
  }

  const getPatientExams = async (options) => {
    try {
      const conditionalExams = await serviceConditions({ exams: options.exams_conditions, patient: patientToAdmission });
      let exams = conditionalExams?.map((exam) => ({
        ...exam,
        children: exam.name,
        value: `${exam.id}-${exam.type}`
      }));

      if (exams.length) {
        for await (const exam of exams) {
          await addPatientService(exam, exam.attached_services);
        }
      }
    } catch (error) {
      console.error(error);
      message.error({ content: `Error al agregar servicio ${error}`, style: { marginTop: '45vh', }, });
    }
  }

  const addPatientServices = async () => {
    if (patientToAdmission) {
      const combinedServices = [
        ...(
          patientToAdmission?.prestations?.filter(prestation => !selectedServices?.includes(prestation))
          || []
        )
      ];

      setSelectedServices((prevServices) => [...prevServices, ...combinedServices]);

      setPatientToAdmission(_patientToAdmission => ({
        ..._patientToAdmission,
        consents: null,
      }));
      if (Object.keys(patientToAdmission).includes('evaluation_type')) {
        serviceForm.setFieldsValue({ 'evaluation_type': (patientToAdmission.evaluation_type && parseInt(patientToAdmission.evaluation_type, 10)) });
      }
      if (Object.keys(patientToAdmission).includes('diabetic')) {
        serviceForm.setFieldsValue({ 'diabetic': patientToAdmission.diabetic });
      }
      if (laboratoryModule) {
        if (patientToAdmission.previousLaboratoryPrestations) {
          setPreviousLaboratoryPrestations(patientToAdmission.previousLaboratoryPrestations);
        }
        if (patientToAdmission.laboratoryPrestations) {
          setLaboratoryPrestations(patientToAdmission.laboratoryPrestations);
        }
        initLaboratoryServices();
      }
      for await (const service of combinedServices) {
        if (service.exams_conditions) {
          await getPatientExams(service);
        }
      }
    }
  }

  useEffect(() => {
    if (serviceForm.getFieldValue('evaluation_type') || serviceForm.getFieldValue('diabetic')) {
      serviceForm.setFieldsValue({ 'evaluation_type': 0 });
      serviceForm.setFieldsValue({ 'diabetic': false });
    }

    addPatientServices();

    window.addEventListener('beforeunload', confirmRefreshPage);
    return () => {
      window.removeEventListener('beforeunload', confirmRefreshPage);
    }
  }, [currentStep]);

  const initLaboratoryServices = async () => {
    const prestations = patientToAdmission.prestations;
    const previousPrestations = patientToAdmission.previousPrestations;

    let laboratoryServicesToAdd = [];
    let previousLabs = [];

    if (prestations) {
      const prestationsLabs = await getLaboratoryServices(prestations);
      laboratoryServicesToAdd = [...laboratoryServicesToAdd, ...prestationsLabs];
    }
    if (previousPrestations) {
      previousLabs = await getLaboratoryServices(previousPrestations);
      setPreviousLaboratoryPrestations(uniqBy(previousLabs, 'name'));
    }
    setLaboratoryPrestations(laboratoryServicesToAdd);
    setPatientToAdmission(patientToAdmission => {
      return {
        ...patientToAdmission,
        previousLaboratoryPrestations: previousLabs,
        laboratoryPrestations: laboratoryServicesToAdd,
      }
    });
  }

  const getLaboratoryServices = async (prestations) => {
    let laboratoryServices = [];
    laboratoryServices = getLaboratoryExamsByPrestations(prestations);
    laboratoryServices.map((general) => {
      general.disabled = true;
      general.selected = true;
      return general;
    })
    return laboratoryServices;
  }

  const getLaboratoryExamsByPrestations = (prestations) => {
    let laboratoryExams = [];
    prestations.forEach((prestation) => {
      if (prestation.exams) {
        prestation.exams.forEach((exam) => {
          exam.prestation_id = prestation.id
          if (exam.type === 'laboratory_integration' || [exam.prestation_type, exam.type].includes('laboratory')) {
            exam.type = 'laboratory';
            laboratoryExams.push(exam);
          }
        });
      }
    })
    return laboratoryExams;
  }

  const getLaboratoryPrestations = async (serviceId, serviceType, providerCode) => {
    try {
      let payload = {
        provider_code: providerCode
      };
      if (serviceType === 'Exam') {
        payload.exam_id = serviceId
      } else {
        payload.battery_id = serviceId
      }
      const laboratoryResponse = await admissionService.getLaboratoryPrestations(payload);
      const result = (laboratoryResponse) ? formatLaboratoryPrestations(laboratoryResponse, 'laboratory', serviceId) : [];
      return result;
    } catch (error) {
      console.error(error);
      message.error({ content: "Error al traer los exámenes de laboratorio", style: { marginTop: '45vh' } });
      return [];
    }
  };

  const formatLaboratoryPrestations = (labPrestations, type, id) => {
    return labPrestations.map((prestation) => {
      prestation.prestation_type = type;
      prestation.prestation_id = id;
      delete prestation.type;
      return prestation;
    })
  }

  const fetchServices = debounce(async (name) => {
    if (hasEnoughSearchLength(name)) {
      setFetchLoading(true);
      const response = await admissionService.getServices({ query: name, branch_office_id: reservation.branch_office_id });

      setServicesList(response.data || response);
      setFetchLoading(false);
    } else {
      setServicesList([])
    }
  }, PRESTATIONS_SEARCH_DEBOUNCE_MS);

  const handleOnChangeSearch = async (value, options) => {
    if (options?.attached_services?.length && countrySettings.patientServices.addAttachedServices[countryCode]) {
      await addPatientService(options, options.attached_services);
    } else {
      await addPatientService(options);
    }

    if (options.exams_conditions) {
      await getPatientExams(options);
    }

    if (value) {
      serviceForm.setFieldsValue({ 'serviceList': value });
    } else {
      serviceForm.setFieldsValue({ 'serviceList': null });
      setServicesList([]);
    }
  };

  const addPatientService = async (options, attachedServices) => {
    try {
      if (options) {
        let existsOnPrevious = patientToAdmission?.previousPrestations?.some(item =>
          `${item.id}-${item.type}` === options.value
        );
        let existsOnSelected = selectedServices?.some(item =>
          `${item.id}-${item.type}` === options.value
        );

        if (existsOnSelected || existsOnPrevious) {
          utils.messageAlert('error', 'El servicio ya fue agregado');
        } else {
          let attachedServicesToAdd = [];
          if (attachedServices?.length) {

            attachedServicesToAdd = attachedServices?.map((exam) => ({
              id: exam.id,
              name: exam.children || exam.name,
              type: exam.type,
              sale_amount: exam.sale_amount,
              department_id: exam.department_id,
              currency_id: options.currency_id,
              consents: exam.consents,
              exam_type: exam.exam_type,
              medical_form_type: exam.medical_form_type || 'health',
              exams: [],
            })).filter((attached) => (
              !patientToAdmission?.previousPrestations?.some(item =>
                `${item.id}-${item.type}` === `${attached.id}-${attached.type}`
              )
              && !selectedServices?.some(item =>
                `${item.id}-${item.type}` === `${attached.id}-${attached.type}`
              )
            ))
          }

          let serviceToAdd = {
            id: options.id,
            name: options.children,
            type: options.type,
            sale_amount: options.sale_amount,
            department_id: options.department_id,
            currency_id: options.currency_id,
            consents: options.consents,
            exam_type: options.exam_type,
            medical_form_type: options.medical_form_type,
            exams: [],
            battery_exams: options.battery_exams || []
          }
          if (!laboratoryModule) {
            const services = [serviceToAdd, ...attachedServicesToAdd];
            setSelectedServices((prevServices) => [...prevServices, ...services]);

            setPatientToAdmission(_patientToAdmission => ({
              ..._patientToAdmission,
              consents: null,
              prestations: [...services, ..._patientToAdmission.prestations]
            }));
          } else {
            setLaboratoryLoading(true);
            const laboratoryExams = await getLaboratoryPrestations(options.id, options.type, providerCode);
            let laboratoryServicesToAdd = laboratoryExams?.length ? laboratoryExams.map((prestation) => {
              prestation.disabled = true;
              prestation.selected = true;
              return prestation;
            }) : [];

            serviceToAdd.exams = laboratoryExams;
            const services = [serviceToAdd, ...attachedServicesToAdd];
            const newLaboratoryPrestations = [...laboratoryPrestations, ...laboratoryServicesToAdd];

            setLaboratoryPrestations(newLaboratoryPrestations);

            setSelectedServices((prevServices) => [...prevServices, ...services]);

            setPatientToAdmission(_patientToAdmission => ({
              ..._patientToAdmission,
              consents: null,
              laboratoryPrestations: newLaboratoryPrestations,
              prestations: [...services, ..._patientToAdmission.prestations],
            }));

            setLaboratoryLoading(false);
          }
          serviceForm.setFieldsValue({ patientService: null });
        }
      }
    } catch (error) {
      console.error(error);
      message.error({ content: `Error al agregar servicio ${error}`, style: { marginTop: '45vh', }, });
    }
  };

  const removePatientService = (serviceId) => {
    const newSelectedServices = selectedServices.filter(item =>
      `${item.id}-${item.type}` !== serviceId
    );

    let newLaboratoryPrestations = [];

    if (laboratoryModule) {
      newLaboratoryPrestations = laboratoryPrestations?.length ? laboratoryPrestations.filter(item => (item.prestation_id !== parseInt(serviceId))) : [];
      setLaboratoryPrestations(newLaboratoryPrestations);
    }

    setSelectedServices(newSelectedServices);

    setPatientToAdmission(patientToAdmission => {
      return {
        ...patientToAdmission,
        laboratoryPrestations: (laboratoryModule) ? newLaboratoryPrestations : [],
        prestations: newSelectedServices
      }
    });
  };

  const handleRadioButton = ({ target: { value: newEvaluationType } }) => {
    let updatedProfession = patientToAdmission.profession;
    if (professionRequiredByCountry) {
      const professionRequired = isProfessionRequiredByEvaluationType(newEvaluationType, countryCode, evaluationTypes);
      const hasProfession = !!patientToAdmission.profession?.trim();
      if (!professionRequired) {
        updatedProfession = '';
      }
      setShowProfessionWarning(professionRequired && !hasProfession);
    }

    setPatientToAdmission(prev => ({
      ...prev,
      evaluation_type: newEvaluationType,
      hasPrefilledEvaluationType: false,
      profession: updatedProfession,
    }));
  };

  const handleDiabeticButton = async (event) => {
    const isDiabetic = (event.target.value === 'true');
    serviceForm.setFieldsValue({ 'diabetic': isDiabetic });
    setPatientToAdmission(patientToAdmission => { return { ...patientToAdmission, diabetic: isDiabetic } });

    if (!isDiabetic) {
      removePatientService(`${diabeticId}-Exam`);
    } else {
      let existsOnSelected;
      let existOnPrevious;

      if (selectedServices && selectedServices.length) {
        existsOnSelected = selectedServices.find(item => parseInt(item.id) === diabeticId);
      }

      if (patientToAdmission.previousPrestations && patientToAdmission.previousPrestations.length) {
        existOnPrevious = patientToAdmission.previousPrestations.find(item => parseInt(item.id) === diabeticId);
      }

      if (!existsOnSelected &&
        !(existsOnSelected && !existsOnSelected.length) &&
        !existOnPrevious && !(existOnPrevious && !existOnPrevious.length)) {
        const options = await initDiabeticPrestation();
        await addPatientService(options);
      }
    }
  };

  const getDiabeticPrestation = async ({ examId }) => {
    if (examId) {
      setFetchLoading(true);
      try {
        let params = { examId };
        if (providerCode) {
          params.providerCode = providerCode;
        }
        const response = await admissionService.getService(params);
        return response;
      } catch (error) {
        console.error(error);
        message.error({ content: "Error al traer examen preventivo de diabetes", style: { marginTop: '45vh' } });
        return [];
      } finally {
        setFetchLoading(false);
      }
    } else {
      setServicesList([])
      return [];
    }
  };

  const initDiabeticPrestation = async () => {
    const [diabeticPrestation] = await getDiabeticPrestation({ examId: diabeticId });
    if (diabeticPrestation) {
      diabeticPrestation.type = diabeticPrestation.exam_type;

      const options = {
        id: diabeticPrestation.id,
        children: diabeticPrestation.name,
        type: 'Exam',
        sale_amount: diabeticPrestation.sale_amount,
        department_id: 1,
        consents: [],
        diabetic: true,
        exam_type: diabeticPrestation.exam_type,
        medical_form_type: diabeticPrestation.medical_form_type || 'health'
      }

      return options;
    }
  }

  const validateProfessionField = () => {
    const evaluationType = serviceForm.getFieldValue('evaluation_type');
    const professionIsRequired = isProfessionRequiredByEvaluationType(evaluationType, countryCode, evaluationTypes);
    const profession = patientToAdmission.profession;

    const shouldRequireProfession = professionRequiredByCountry && professionIsRequired;

    if (shouldRequireProfession && (!profession || profession.trim() === "")) {
      utils.messageAlert('error', "Debes completar el campo 'Cargo' en el paso anterior.");
      setShowProfessionWarning(true);
      return false;
    }
    return true;
  }

  const validatePatientServices = () => {
    serviceForm.validateFields();
    if (!serviceForm.getFieldValue('evaluation_type') && serviceForm.getFieldValue('evaluation_type') !== 0) {
      utils.messageAlert('error', `Seleccione estado de contrato del ${t('admissionPage.patientServices.patient').toLocaleLowerCase()}`);
      return false;
    }

    if (serviceForm.getFieldValue('diabetic') === null) {
      utils.messageAlert('error', `Seleccione si el ${t('admissionPage.patientServices.patient').toLocaleLowerCase()} es diabético`);
      return false;
    }

    if (selectedServices.length === 0) {
      utils.messageAlert('error', `Debe seleccionar al menos 1 ${t('admissionPage.patientServices.service').toLocaleLowerCase()}`);
      return false;
    }
    if (!validateProfessionField()) {
      return false;
    };

    return true;
  };

  const handleNextStep = () => {
    if (validatePatientServices()) {
      serviceForm.resetFields();
      setCurrentStep(2);
    }
  };

  return (
    <Row justify="center" className="admission-settings__patient-form mt-50">
      <Col lg={18} xs={22}>
        {
          (patientToAdmission.admissionPatientId) &&
          <Row justify="center" className="alertAdmission mb-20">
            <Col lg={22} xs={24} className="previousAdmissionAlert">
              <Row className="alert">
                <Col lg={1} xs={2}>
                  <svg xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" width={50} height={50} viewBox="0 0 50 50">
                    <defs>
                      <clipPath id="clip-Mesa_de_trabajo_1">
                        <rect width={50} height={50} />
                      </clipPath>
                    </defs>
                    <g id="Mesa_de_trabajo_1" data-name="Mesa de trabajo – 1" clipPath="url(#clip-Mesa_de_trabajo_1)">
                      <g id="Grupo_755" data-name="Grupo 755" transform="translate(-259 -890)">
                        <circle id="Elipse_6" data-name="Elipse 6" cx={25} cy={25} r={25} transform="translate(259 890)" fill="#5b356d" />
                        <text id="_" data-name="!" transform="translate(283 928)" fill="#fff" fontSize={35} fontWeight={700}><tspan x="-5" y={0}>!</tspan></text>
                      </g>
                    </g>
                  </svg>
                </Col>
                <Col lg={23} xs={22}>
                  <p>
                    <strong>El {t('admissionPage.patientServices.patient').toLocaleLowerCase()} seleccionado ya tiene un ingreso</strong>. A continuación podrás agregar {t('admissionPage.patientServices.services').toLocaleLowerCase()} a su admisión.
                  </p>
                </Col>
              </Row>
            </Col>
          </Row>
        }
        <Row justify="center">
          <Col
            lg={22} xs={24}
            className="form-wrapper pt-30 pb-30 pl-lg-70 pl-md-20 pl-sm-20 pl-xs-20 pr-lg-70 pr-md-20 pr-sm-20 pr-xs-20"
          >
            <Form form={serviceForm} size="large">
              <Spin spinning={fetchLoading}>
                <Row>
                  <Col lg={24} xs={24}>
                    <Row>
                      <Col lg={24} xs={24}>
                        <h2 className='mb-20'>
                          {t('admissionPage.patientServices.selectServices')}
                        </h2>
                      </Col>
                      <Col lg={24} xs={24}>
                        <Form.Item
                          name="patientService"
                          style={{ width: '100%' }}
                        >
                          <Select
                            id='patientService'
                            className='cmt-select'
                            style={{ width: '100%' }}
                            placeholder={t('admissionPage.patientServices.servicePlaceholder')}
                            showArrow
                            allowClear
                            showSearch
                            filterOption={false}
                            notFoundContent={null}
                            onSearch={fetchServices}
                            onChange={handleOnChangeSearch}
                            onClear={() => setServicesList([])}
                          >
                            {
                              servicesList?.filter((service) =>
                                !selectedServices?.some((selected) =>
                                  selected.id === service.id
                                  && selected.type === service.type
                                ))
                                .filter((service) =>
                                  !patientToAdmission?.previousPrestations?.some((previous) =>
                                    previous.id === service.id
                                    && previous.type === service.type
                                  ))
                                .map((service, index) =>
                                  <Option
                                    key={index}
                                    id={service.id}
                                    value={`${service.id}-${service.type}`}
                                    type={service.type}
                                    sale_amount={service.sale_amount}
                                    currency_id={service.currency_id}
                                    consents={service.consents || ['normal', 'drugs']}
                                    exams_conditions={service.exams_conditions}
                                    attached_services={service.attached_services}
                                    exam_type={service.exam_type}
                                    medical_form_type={service.medical_form_type}
                                    battery_exams={service.battery_exams}
                                  >
                                    {service.name}
                                  </Option>
                                )
                            }
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col lg={24} xs={24}>
                        {
                          selectedServices?.map((service, index) =>
                            <Tag
                              key={index}
                              disabled={service.disabled}
                              closable
                              visible
                              onClose={() => {
                                removePatientService(`${service.id}-${service.type}`)
                              }}
                              className='custom-tag mr-15 mb-15'
                            >
                              {service.name}
                            </Tag>
                          )
                        }
                      </Col>
                    </Row>
                  </Col>
                  <Col lg={12} xs={24} style={{ marginTop: patientToAdmission.hasPrefilledEvaluationType ? 40 : 0 }}>
                    <Row className='patients-service'>
                      {patientToAdmission.hasPrefilledEvaluationType && (
                        <Col className='mr-10'>
                          <span
                            className='icon-alert-triangle-bold'
                            onMouseEnter={() => setShowEvaluationTypeTooltip(true)}
                            onMouseLeave={() => setShowEvaluationTypeTooltip(false)}
                          />
                        </Col>
                      )}
                      <Col className='mr-30 evaluation-type-label'>
                        <p className='mb-0'>
                          {t('admissionPage.patientServices.hiredOrServiceType')}
                        </p>
                        {patientToAdmission.hasPrefilledEvaluationType && (
                          <Tooltip
                            title="Dato ingresado en pre-admisión"
                            placement="top"
                            color="#223A70"
                            overlayClassName="evaluation-type-tooltip"
                            open={showEvaluationTypeTooltip}
                          >
                            <span style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }} />
                          </Tooltip>
                        )}
                      </Col>
                      <Col>
                        <Form.Item className="m-0 p-0" name="evaluation_type" rules={[{
                          required: true,
                          message: t('admissionPage.patientServices.evaluationTypePlaceholder')
                        }]}
                          onChange={handleRadioButton}
                        >
                          <Radio.Group className="cmt-radio" id='evaluation_type'>
                            {
                              evaluationTypes[countryCode].map((type) =>
                                <Radio value={type.value}>{type.text}</Radio>
                              )
                            }
                          </Radio.Group>
                        </Form.Item>
                        {showProfessionWarning && (
                          <Alert
                            message={t('admissionPage.patientForm.professionNeededWarning')}
                            type="warning"
                            showIcon
                            className="mt-10"
                          />
                        )}
                      </Col>
                    </Row>
                  </Col>
                  <Col lg={12} xs={24} style={{ marginTop: patientToAdmission.hasPrefilledEvaluationType ? 40 : 0 }}>
                    <Row className='patients-service'>
                      <Col className='mr-30'>
                        <p className='mb-0'>¿{t('admissionPage.patientServices.patient')} diabético?</p>
                      </Col>
                      <Col>
                        <Form.Item
                          className="m-0 p-0"
                          name="diabetic"
                          rules={[{
                            required: true,
                            message: 'Campo es requerido'
                          }]}
                          onChange={handleDiabeticButton}
                        >
                          <Radio.Group className="cmt-radio" id='diabetic'>
                            <Radio value={false}>No</Radio>
                            <Radio value={true}>Sí</Radio>
                          </Radio.Group>
                        </Form.Item>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              </Spin>
            </Form>
          </Col>

          {
            (patientToAdmission?.admissionPatientId) &&
            <Col lg={22} xs={24} className="previous-prestations">
              <Row className="box pt-30 pb-30 pl-lg-70 pl-md-20 pl-sm-20 pl-xs-20 pr-lg-70 pr-md-20 pr-sm-20 pr-xs-20">
                <Form form={serviceForm} size="large">
                  <Spin spinning={fetchLoading}>
                    <Col lg={24} xs={24} className="title">
                      <strong>{t('admissionPage.patientServices.services')} ingresadas para este {t('admissionPage.patientServices.patient').toLocaleLowerCase()}</strong>
                    </Col>
                    <Col style={{ marginTop: '30px', marginLeft: '40px' }} lg={{ order: 4, span: 24 }} xs={{ order: 3, span: 24 }}>
                      {
                        patientToAdmission?.previousPrestations?.map((service, index) => {
                          return (
                            <Tag
                              key={index}
                              visible
                              onClose={() => removePatientService(`${service.id}-${service.type}`)}
                              className={(service.disabled) ? 'custom-tag-disabled mr-15 mb-15' : 'custom-tag mr-15 mb-15'}
                            >
                              {service.name}
                            </Tag>
                          )
                        })
                      }
                    </Col>
                  </Spin>
                </Form>
              </Row>
            </Col>
          }
        </Row>

        <Spin className="cmt-spin" spinning={laboratoryLoading}>
          {
            laboratoryModule &&
            <LaboratoryExams
              patientToAdmission={patientToAdmission}
              setPatientToAdmission={setPatientToAdmission}
              currentStep={currentStep}
              laboratoryPrestations={viewLaboratoryPrestations(laboratoryPrestations)}
              setLaboratoryPrestations={setLaboratoryPrestations}
              previousLaboratoryPrestations={previousLaboratoryPrestations}
              setPreviousLaboratoryPrestations={setPreviousLaboratoryPrestations}
              laboratoryModule={laboratoryModule}
              loading={fetchLoading}
              providerCode={providerCode}
            />
          }

        </Spin>
        <Row justify="center">
          <Col lg={22} xs={24} className='mt-50 mb-60'>
            <Row justify="end">
              <Col xl={3} lg={4} xs={24}>
                <Button
                  onClick={handleNextStep}
                  className='cmt-button primary'
                  type='primary'
                  size='large'
                  block
                >
                  Siguiente
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

export default PatientServices;
